import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Info } from "lucide-react";
import { MENU_CATEGORIES } from "@/data/menu";
import { getItemAllergens } from "@/data/menuAugment";
import { ALLERGENS } from "@/data/allergens";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function MenuSection() {
  const { add, setOpen: setCartOpen } = useCart();
  const { lang, t } = useLang();
  const [activeCat, setActiveCat] = useState(MENU_CATEGORIES[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          const id = e.target.getAttribute("data-cat-id");
          if (id) setActiveCat(id);
        }
      }),
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    MENU_CATEGORIES.forEach((c) => {
      const el = document.getElementById(`cat-${c.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToCat = (id) => {
    const el = document.getElementById(`cat-${id}`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleAdd = (fullId, option) => {
    add(fullId, option);
    setCartOpen(true);
  };

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">{t("menu_overline")}</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
              data-testid="menu-title"
            >
              {t("menu_title_1")} <span className="text-ember">{t("menu_title_end")}</span>.
            </motion.h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg leading-relaxed">
            {t("menu_subtitle")}
          </p>
        </div>

        {/* Allergen legend */}
        <div className="mb-10 rounded-2xl bg-ash/40 border border-white/10 p-4 md:p-5" data-testid="allergens-legend">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-gold mt-1 shrink-0" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-gold font-medium mb-2">{t("menu_allergens_title")}</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(ALLERGENS).map(([key, a]) => (
                  <span key={key} className="inline-flex items-center gap-1 text-[10px] text-white/70 bg-black/40 border border-white/10 px-2 py-1 rounded-full">
                    <span className="text-gold font-mono font-bold">{a.code}</span>
                    {a.label}
                  </span>
                ))}
              </div>
              <div className="text-[10px] text-white/40 mt-3">
                Per informazioni su allergeni e intolleranze chiedere al personale.
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Category Tabs */}
        <div className="sticky top-20 z-30 -mx-6 md:mx-0 px-6 md:px-0 py-3 mb-10 bg-ink/85 backdrop-blur-xl border-y border-white/5" data-testid="menu-tabs">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {MENU_CATEGORIES.map((c) => {
              const active = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => scrollToCat(c.id)}
                  data-testid={`menu-tab-${c.id}`}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors duration-300 ${
                    active
                      ? "bg-ember border-ember text-white"
                      : "bg-transparent border-white/10 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-20">
          {MENU_CATEGORIES.map((cat) => {
            return (
              <div key={cat.id} id={`cat-${cat.id}`} data-cat-id={cat.id} data-testid={`menu-category-${cat.id}`}>
                <div className="flex items-end justify-between mb-8 md:mb-10 gap-6 border-b border-white/5 pb-6">
                  <div>
                    <div className="text-3xl md:text-4xl font-display text-gold/60 mb-2">{cat.kanji}</div>
                    <h3 className="font-display text-3xl md:text-4xl font-semibold text-white">{cat.name}</h3>
                    {cat.subtitle && <p className="mt-2 text-sm md:text-base text-white/50">{cat.subtitle}</p>}
                  </div>
                  <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full bg-black/40 border border-white/10 text-[10px] uppercase tracking-[0.18em] text-gold">
                    {cat.tag}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {cat.items.map((item, i) => {
                    const fullId = `${cat.id}-${item.n}`;
                    const allergens = getItemAllergens(cat.id, item.n);
                    return (
                      <motion.div
                        key={fullId}
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-30px" }}
                        transition={{ duration: 0.35, delay: Math.min(i * 0.015, 0.15) }}
                        className="group relative rounded-2xl bg-ash/50 hover:bg-ash border border-white/5 hover:border-gold/30 p-5 transition-colors duration-300"
                        data-testid={`menu-item-${fullId}`}
                      >
                        <div className="flex items-start gap-4">
                          <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-black/40 border border-white/10 text-[11px] font-mono text-gold">
                            {item.n}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h4 className="font-display text-base md:text-lg font-medium text-white leading-snug">
                                  {lang === "en" && item.nameEn ? item.nameEn : item.name}
                                </h4>
                                {(lang === "en" ? item.name : item.nameEn) && <div className="text-xs text-white/40 italic mt-0.5">{lang === "en" ? item.name : item.nameEn}</div>}
                                {item.desc && (
                                  <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">
                                    {item.desc}
                                  </p>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="font-display text-lg md:text-xl text-gold font-semibold whitespace-nowrap">
                                  €{item.price.toFixed(item.price % 1 ? 2 : 0)}
                                </div>
                                {item.unit && <div className="text-[10px] text-white/40 uppercase tracking-widest mt-0.5">{item.unit}</div>}
                              </div>
                            </div>

                          {/* Allergen badges */}
                          {allergens.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`allergens-${fullId}`}>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="inline-flex items-center gap-1 text-[9px] text-white/40 hover:text-white/70 uppercase tracking-widest transition-colors">
                                    <Info size={10} /> Allergeni
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="bg-ink border-white/10 text-white text-xs w-64" align="start">
                                  <div className="font-medium text-gold mb-2">Contiene:</div>
                                  <ul className="space-y-1">
                                    {allergens.map((k) => (
                                      <li key={k} className="text-white/70">
                                        <span className="text-gold font-mono">{ALLERGENS[k]?.code}</span> — {ALLERGENS[k]?.full}
                                      </li>
                                    ))}
                                  </ul>
                                </PopoverContent>
                              </Popover>
                              {allergens.map((k) => (
                                <span key={k} title={ALLERGENS[k]?.full}
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/40 border border-gold/25 text-[9px] text-gold font-mono font-bold">
                                  {ALLERGENS[k]?.code}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Add controls */}
                          {item.options ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {item.options.map((opt) => (
                                <button
                                  key={opt}
                                  onClick={() => handleAdd(fullId, opt)}
                                  data-testid={`menu-add-${fullId}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-black/40 border border-white/10 hover:border-ember hover:text-white text-white/75 transition-colors"
                                >
                                  <Plus size={12} /> {opt}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => handleAdd(fullId)}
                                data-testid={`menu-add-${fullId}`}
                                aria-label={`Aggiungi ${item.name}`}
                                className="w-9 h-9 rounded-full grid place-items-center bg-ember hover:bg-emberDeep text-white transition-colors shadow-[0_6px_20px_-6px_rgba(200,16,46,0.6)]"
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </section>
  );
}
